import { useQuery } from 'react-query';
import { useContext, useMemo } from 'react';
import Booking from '@solvhealth/types/interfaces/Booking';
import AuthenticationContext from '~/core/login/AuthenticationContext';
import { apiGetJson } from '../core/dapi';
import { DAPI_HOST } from '../config';
import { validateFamilyBooking } from '../util/validateFamilyBooking';
import { EMPTY_ARRAY } from '~/core/util/array';

interface FamilyBookings {
  bookings: Booking[];
}

export function useQueryFamilyBookings(bookingFamilyId: string) {
  const { isLoggedIn } = useContext(AuthenticationContext);
  const familyBookingsEndpoint = isLoggedIn ? 'booking-families' : 'public-booking-families';

  const { data: bookings, ...rest } = useQuery(
    ['query_child_bookings', bookingFamilyId, familyBookingsEndpoint],
    async () => {
      if (!bookingFamilyId) {
        return null;
      }

      const {
        data: { bookings },
      } = await apiGetJson<FamilyBookings>(
        `${DAPI_HOST}/v1/${familyBookingsEndpoint}/${bookingFamilyId}`,
        { camelCase: true }
      );

      return bookings;
    }
  );

  const bookingsInFamily = useMemo(
    () =>
      [...(bookings || [])].sort((a, b) => {
        const familyA = validateFamilyBooking(a);
        const familyB = validateFamilyBooking(b);

        if (familyA.isAuxiliary && familyB.isPrimary) {
          return 1;
        }

        if (familyA.isPrimary && familyB.isAuxiliary) {
          return -1;
        }

        if (familyA.isAuxiliary && familyB.isAuxiliary) {
          if (a.paperworkStatus === 'complete' && b.paperworkStatus !== 'complete') {
            return -1;
          }
          if (a.paperworkStatus !== 'complete' && b.paperworkstatus === 'complete') {
            return 1;
          }
        }

        return 0;
      }),
    [bookings]
  );

  const primaryBooking = bookingsInFamily.find(
    (booking) => booking.bookingFamilyMemberType === 'primary'
  );

  return {
    bookingsInFamily,
    primaryBooking: primaryBooking ?? null,
    ...rest,
  };
}
